import { useState, type FormEvent } from 'react'
import { Send, Check, AlertCircle } from 'lucide-react'
import { Input } from '../ui/input'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    email: '',
    message: '',
  })

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Custom validation
    const errors = {
      name: '',
      email: '',
      message: '',
    }

    if (!formData.name.trim()) {
      errors.name = 'Please fill in this field.'
    }

    if (!formData.message.trim()) {
      errors.message = 'Please fill in this field.'
    }

    if (!formData.email.trim()) {
      errors.email = 'Please fill in this field.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address.'
    }

    if (errors.name || errors.email || errors.message) {
      setValidationErrors(errors)
      return
    }

    setValidationErrors({ name: '', email: '', message: '' })
    setStatus('loading')
    setErrorMessage('')

    try {
      const response = await fetch('/api/plain/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        setStatus('success')
        // Reset form after showing success
        setTimeout(() => {
          setFormData({ name: '', email: '', message: '' })
          setStatus('idle')
        }, 3000)
      } else {
        setStatus('error')
        setErrorMessage(data.error || 'Failed to send message. Please try again.')
      }
    } catch (error) {
      setStatus('error')
      setErrorMessage('Network error. Please check your connection and try again.')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
    // Clear validation error when user starts typing
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors({
        ...validationErrors,
        [name]: '',
      })
    }
  }

  return (
    <div className="max-w-2xl">
      {/* Greeting */}
      <div className="mb-6">
        <p className="text-lg text-gray-900 font-sans">👋 Hi there, how can we help?</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Message Textarea */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Bugs? Feature requests? Fan mail?"
            rows={5}
            maxLength={500}
            disabled={status === 'loading' || status === 'success'}
            className={`flex w-full rounded-md border ${
              validationErrors.message ? 'border-red-500 focus-visible:ring-red-500' : 'border-input focus-visible:ring-ring'
            } bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none font-sans`}
          />
          {validationErrors.message ? (
            <p className="text-xs text-red-600 mt-1 font-sans">{validationErrors.message}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1 font-sans text-right">{formData.message.length}/500 characters</p>
          )}
        </div>

        {/* Name Input */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
            Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Your name"
            disabled={status === 'loading' || status === 'success'}
            className={`font-sans ${
              validationErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''
            }`}
          />
          {validationErrors.name && (
            <p className="text-xs text-red-600 mt-1 font-sans">{validationErrors.name}</p>
          )}
        </div>

        {/* Email Input */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
            Email <span className="text-red-500">*</span>
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your.email@example.com"
            disabled={status === 'loading' || status === 'success'}
            className={`font-sans ${
              validationErrors.email ? 'border-red-500 focus-visible:ring-red-500' : ''
            }`}
          />
          {validationErrors.email ? (
            <p className="text-xs text-red-600 mt-1 font-sans">{validationErrors.email}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1 font-sans">
              We'll only use your email to respond to your message. See our{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Privacy Policy
              </a>
            </p>
          )}
        </div>

        {/* Error Message */}
        {status === 'error' && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-600 font-sans">{errorMessage}</p>
          </div>
        )}

        {/* Success Message */}
        {status === 'success' && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-600 font-sans">Message sent successfully! We'll get back to you soon.</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-sans"
        >
          {status === 'loading' ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending...
            </>
          ) : status === 'success' ? (
            <>
              <Check className="w-4 h-4" />
              Sent!
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Message
            </>
          )}
        </button>
      </form>

      {/* Powered by Plain */}
      <a
        href="https://www.plain.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-center gap-1.5 hover:opacity-80 transition-opacity"
      >
        <span className="text-xs text-gray-500 font-sans">Powered by</span>
        <img
          src="/plain_logo.png"
          alt="Plain"
          className="h-4 opacity-70"
        />
      </a>
    </div>
  )
}
